/**
 * The customer box view (#804): one row per physical box, joining the JSS
 * machine record to its Kubernetes node, with SideroLink, Talos API and
 * kubelet health reported separately.
 *
 * Why separately: "last seen" used to be the SideroLink handshake alone, so a
 * box whose Talos API was dead still looked alive. A wedged DGX Spark held
 * its tunnel and refused port 50000 for 20+ minutes while the console showed
 * it healthy (#789). Each indicator here reads one layer's own signal and
 * names the failure when it isn't healthy.
 *
 * Everything in this module is pure (clock passed in) so the rules are unit
 * tested without rendering.
 */
import type { ClusterNode, Machine, MachineRole } from '@/types';
import { describeMachineStatus } from '@/lib/machineStatus';

// ---- Thresholds (mirrors of deployment-service constants) ----

/**
 * A SideroLink handshake within this window means the tunnel is up.
 * WireGuard re-handshakes every 120 s and drops keys after 180 s without one
 * (machineLiveness.ts SIDEROLINK_FRESH_WINDOW_MS).
 */
export const SIDEROLINK_FRESH_MS = 180_000;

/** apid results older than this are stale (siteReadiness TALOS_LIVENESS_STALE_MS). */
export const APID_STALE_MS = 10 * 60_000;

/** The heartbeat monitor's timeout for a kubelet heartbeat (heartbeatMonitor.ts). */
export const KUBELET_HEARTBEAT_TIMEOUT_MS = 120_000;

/**
 * Statuses in which cluster-monitor probes apid (clusterMonitor.ts
 * MONITORED_STATUSES). Outside them `apidState` is whatever the last probe
 * left — after a reset that can be a stale `serving` — so it isn't shown.
 */
const APID_PROBED_STATUSES: ReadonlySet<string> = new Set([
  'ready', 'bootstrapping', 'joining', 'upgrading', 'cordoned', 'error',
]);

/** Statuses in which the box should have a Kubernetes node. */
const NODE_EXPECTED_STATUSES: ReadonlySet<string> = new Set([
  'ready', 'cordoned', 'upgrading', 'installing_prereqs',
]);

// ---- Join ----

export interface Box {
  /** Stable React key: the machine id, or `node:<name>` for a node JSS has no record of. */
  key: string;
  hostname: string;
  machine: Machine | null;
  node: ClusterNode | null;
  role: MachineRole | null;
}

export interface JoinedBoxes {
  boxes: Box[];
  /** Decommissioned machines left out of the list (none of them has a node). */
  hiddenDecommissioned: number;
}

const ROLE_ORDER: Record<string, number> = { controlplane: 0, worker: 1 };

function compareBoxes(a: Box, b: Box): number {
  // Boxes JSS knows first, then nodes it doesn't; control planes before
  // workers; then by name. Deliberately not by health: rows that reorder
  // on every poll are hard to act on.
  const known = Number(!a.machine) - Number(!b.machine);
  if (known !== 0) return known;
  const role = (ROLE_ORDER[a.role ?? ''] ?? 2) - (ROLE_ORDER[b.role ?? ''] ?? 2);
  if (role !== 0) return role;
  return a.hostname.localeCompare(b.hostname, undefined, { numeric: true });
}

/**
 * Join a site's machines to its Kubernetes nodes by `ClusterNode.machineId`
 * (deployment-service matches node → machine by hostname, then InternalIP).
 *
 * - Every machine that isn't decommissioned is a box, with or without a node.
 * - A node pointing at no known machine is its own box, so nothing in the
 *   cluster is hidden. So is a second node claiming an already-matched
 *   machine (a stale Node object, say).
 * - A decommissioned machine is only shown when a node still points at it:
 *   that box is in the cluster although JSS retired it.
 *
 * `nodes` is undefined while the cluster API is loading or unreachable; the
 * machines are still listed, just without node data.
 */
export function joinBoxes(machines: Machine[], nodes: ClusterNode[] | undefined): JoinedBoxes {
  const machineById = new Map(machines.map((m) => [m.id, m]));
  const nodeByMachine = new Map<string, ClusterNode>();
  const orphanNodes: ClusterNode[] = [];

  for (const n of nodes ?? []) {
    if (n.machineId && machineById.has(n.machineId) && !nodeByMachine.has(n.machineId)) {
      nodeByMachine.set(n.machineId, n);
    } else {
      orphanNodes.push(n);
    }
  }

  const boxes: Box[] = [];
  let hiddenDecommissioned = 0;
  for (const m of machines) {
    const node = nodeByMachine.get(m.id) ?? null;
    if (m.status === 'decommissioned' && !node) {
      hiddenDecommissioned += 1;
      continue;
    }
    boxes.push({ key: m.id, hostname: m.hostname || node?.name || m.id, machine: m, node, role: m.role ?? node?.role ?? null });
  }
  for (const n of orphanNodes) {
    boxes.push({ key: `node:${n.name}`, hostname: n.name, machine: null, node: n, role: n.role ?? null });
  }

  boxes.sort(compareBoxes);
  return { boxes, hiddenDecommissioned };
}

// ---- Indicators ----

/**
 * ok / warn / fail / unknown are health; `na` means the layer isn't expected
 * yet (a box that hasn't installed has no Talos API to probe).
 */
export type IndicatorState = 'ok' | 'warn' | 'fail' | 'unknown' | 'na';

export interface Indicator {
  key: 'siderolink' | 'talos' | 'kubelet' | 'reconnect';
  name: string;
  state: IndicatorState;
  /** A few words for the row, e.g. `refused`, `up · 12s ago`. */
  short: string;
  /** The full reason, in words. */
  detail: string;
}

/** Compact age: 42s, 7m, 3h, 2d. */
export function formatAge(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function ageMs(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : now - t;
}

export function siderolinkFresh(m: Machine | null, now: number): boolean {
  const age = ageMs(m?.siderolinkLastSeen, now);
  return age !== null && age <= SIDEROLINK_FRESH_MS;
}

export function siderolinkIndicator(m: Machine | null, now: number): Indicator {
  const base = { key: 'siderolink' as const, name: 'SideroLink' };
  if (!m) {
    return { ...base, state: 'na', short: 'not tracked', detail: 'This node has no Jeket machine record, so its management tunnel is not tracked.' };
  }
  const age = ageMs(m.siderolinkLastSeen, now);
  if (age === null) {
    return {
      ...base,
      state: 'unknown',
      short: 'never connected',
      detail: 'The box has not connected over its management tunnel (SideroLink) yet. It connects when it boots the site install image.',
    };
  }
  if (age <= SIDEROLINK_FRESH_MS) {
    return { ...base, state: 'ok', short: `up · ${formatAge(age)} ago`, detail: `Management tunnel up; last handshake ${formatAge(age)} ago.` };
  }
  return {
    ...base,
    state: 'fail',
    short: `down · ${formatAge(age)}`,
    detail:
      `No management-tunnel (SideroLink) handshake for ${formatAge(age)}. The box is powered off, ` +
      'restarting, or has lost its network path to the Jeket cloud.',
  };
}

function lastAnswered(m: Machine, now: number): string {
  const age = ageMs(m.apidLastOkAt, now);
  return age === null ? ' It has never answered.' : ` It last answered ${formatAge(age)} ago.`;
}

function unmetConditions(m: Machine): string {
  const c = m.talosUnmetConditions ?? [];
  if (c.length === 0) return '';
  return ` Unmet: ${c.map((x) => (x.reason ? `${x.name} (${x.reason})` : x.name)).join(', ')}.`;
}

export function talosApiIndicator(m: Machine | null, now: number): Indicator {
  const base = { key: 'talos' as const, name: 'Talos API' };
  if (!m) {
    return { ...base, state: 'na', short: 'not tracked', detail: 'This node has no Jeket machine record, so its Talos API is not probed.' };
  }
  if (m.maintenanceDetected) {
    return {
      ...base,
      state: 'warn',
      short: 'maintenance mode',
      detail:
        "The box is in maintenance mode: Talos is running without this site's configuration, so its API " +
        "doesn't answer with the site's credentials. Setup continues on its own, or with Reinstall.",
    };
  }
  if (!APID_PROBED_STATUSES.has(m.status)) {
    const stage = describeMachineStatus(m.status).label.toLowerCase();
    return {
      ...base,
      state: 'na',
      short: 'not checked yet',
      detail: `Checked once the box has installed and joined; it is ${stage} now.`,
    };
  }
  const checkedAge = ageMs(m.apidCheckedAt, now);
  if (m.apidState === 'unknown' || !m.apidState || checkedAge === null) {
    return {
      ...base,
      state: 'unknown',
      short: 'not probed yet',
      detail: m.apidLastError
        ? `The last Talos API probe could not run: ${m.apidLastError}.`
        : 'Jeket has not probed the Talos API yet. Probes run about once a minute.',
    };
  }
  if (checkedAge > APID_STALE_MS) {
    return {
      ...base,
      state: 'unknown',
      short: `stale · ${formatAge(checkedAge)}`,
      detail: `Last probed ${formatAge(checkedAge)} ago (${m.apidState}); no probe has run since, so this may be out of date.`,
    };
  }

  const tunnelUp = siderolinkFresh(m, now);
  switch (m.apidState) {
    case 'serving': {
      if (m.talosStage && m.talosStage !== 'running') {
        return {
          ...base,
          state: 'warn',
          short: `stage ${m.talosStage}`,
          detail: `The Talos API answers, but Talos reports stage "${m.talosStage}", not running.${unmetConditions(m)}`,
        };
      }
      return {
        ...base,
        state: 'ok',
        short: 'serving',
        detail: `Answered an authenticated call ${formatAge(checkedAge)} ago${m.talosStage ? `; stage ${m.talosStage}` : ''}.`,
      };
    }
    case 'refused':
    case 'timeout': {
      const what = m.apidState === 'refused' ? 'refused the connection on port 50000' : 'did not answer on port 50000';
      const wedge = tunnelUp
        ? ' The management tunnel is up, so the box is running but its Talos API is not: it is likely wedged. Power-cycle it, or reset it to maintenance.'
        : ' The management tunnel is down too, so the box is probably off or cut off from the network.';
      return {
        ...base,
        state: 'fail',
        short: m.apidState === 'refused' ? 'refused' : 'no answer',
        detail: `The Talos API ${what}.${wedge}${lastAnswered(m, now)}${m.apidLastError ? ` (${m.apidLastError})` : ''}`,
      };
    }
    case 'tls_mismatch':
      return {
        ...base,
        state: 'fail',
        short: 'TLS mismatch',
        detail:
          "The box answered, but not with this site's credentials: it was reset or reflashed and is in " +
          `maintenance mode, or its configuration belongs to another site.${lastAnswered(m, now)}` +
          `${m.apidLastError ? ` (${m.apidLastError})` : ''}`,
      };
    default:
      return { ...base, state: 'unknown', short: String(m.apidState), detail: `Unrecognised Talos API state "${m.apidState}".` };
  }
}

/** Where the site's node list stands: loaded, still loading, or unreachable. */
export type NodesState = 'ok' | 'loading' | 'error';

const PRESSURE_CONDITIONS = ['MemoryPressure', 'DiskPressure', 'PIDPressure', 'NetworkUnavailable'];

function heartbeatSuffix(m: Machine | null, now: number): string {
  const age = ageMs(m?.lastHeartbeat, now);
  return age === null ? '' : ` Last kubelet heartbeat ${formatAge(age)} ago.`;
}

export function kubeletIndicator(box: Pick<Box, 'machine' | 'node'>, nodesState: NodesState, now: number): Indicator {
  const base = { key: 'kubelet' as const, name: 'Kubelet' };
  const { machine: m, node } = box;

  if (node) {
    const ready = node.conditions.find((c) => c.type === 'Ready');
    if (!node.ready) {
      const why = [ready?.reason, ready?.message].filter(Boolean).join(': ');
      if (ready?.status === 'Unknown') {
        return {
          ...base,
          state: 'fail',
          short: 'not reporting',
          detail: `The kubelet stopped posting node status${why ? ` (${why})` : ''}.${heartbeatSuffix(m, now)}`,
        };
      }
      return {
        ...base,
        state: 'fail',
        short: ready?.reason ? `NotReady · ${ready.reason}` : 'NotReady',
        detail: `Kubernetes reports the node NotReady${why ? ` — ${why}` : ''}.${heartbeatSuffix(m, now)}`,
      };
    }
    const pressures = node.conditions.filter((c) => c.status === 'True' && PRESSURE_CONDITIONS.includes(c.type));
    if (pressures.length > 0) {
      return {
        ...base,
        state: 'warn',
        short: pressures.map((c) => c.type).join(', '),
        detail: `Ready, but under ${pressures.map((c) => `${c.type}${c.message ? ` (${c.message})` : ''}`).join('; ')}.`,
      };
    }
    const hbAge = ageMs(m?.lastHeartbeat, now);
    const cordon = node.unschedulable ? ' It is cordoned: no new workloads are scheduled onto it.' : '';
    return {
      ...base,
      state: 'ok',
      short: `Ready${node.unschedulable ? ' · cordoned' : ''}${hbAge !== null ? ` · ${formatAge(hbAge)} ago` : ''}`,
      detail: `Kubernetes reports the node Ready.${heartbeatSuffix(m, now)}${cordon}`,
    };
  }

  // No node for this box.
  if (!m) return { ...base, state: 'unknown', short: 'no data', detail: 'No node or machine data.' };
  const expected = NODE_EXPECTED_STATUSES.has(m.status) || (m.status === 'error' && !!m.lastHeartbeat);
  if (!expected) {
    return { ...base, state: 'na', short: 'not joined yet', detail: 'The box joins Kubernetes after it has installed.' };
  }
  if (nodesState === 'loading') {
    return { ...base, state: 'unknown', short: 'checking…', detail: 'Loading the cluster’s node list.' };
  }
  if (nodesState === 'error') {
    return {
      ...base,
      state: 'unknown',
      short: 'cluster unreachable',
      detail: `The site's Kubernetes API could not be reached, so the node's state is unknown.${heartbeatSuffix(m, now)}`,
    };
  }
  return {
    ...base,
    state: 'fail',
    short: 'no node',
    detail: `This box should be in the cluster, but Kubernetes has no node for it.${heartbeatSuffix(m, now)}`,
  };
}

/**
 * Kubelet verdict from the machine row alone, for a view that doesn't load
 * the site's nodes (the cross-site inventory): the kubelet's last heartbeat
 * against the heartbeat monitor's timeout.
 */
export function kubeletHeartbeatIndicator(m: Machine, now: number): Indicator {
  const base = { key: 'kubelet' as const, name: 'Kubelet' };
  const expected = NODE_EXPECTED_STATUSES.has(m.status) || (m.status === 'error' && !!m.lastHeartbeat);
  if (!expected) {
    return { ...base, state: 'na', short: 'not joined yet', detail: 'The box is not in the cluster in its current status.' };
  }
  const age = ageMs(m.lastHeartbeat, now);
  if (age === null) {
    return { ...base, state: 'unknown', short: 'no heartbeat yet', detail: 'No kubelet heartbeat has been recorded for this box.' };
  }
  if (age <= KUBELET_HEARTBEAT_TIMEOUT_MS) {
    return { ...base, state: 'ok', short: `heartbeat · ${formatAge(age)} ago`, detail: `Last kubelet heartbeat ${formatAge(age)} ago.` };
  }
  return {
    ...base,
    state: 'fail',
    short: `no heartbeat · ${formatAge(age)}`,
    detail: `No kubelet heartbeat for ${formatAge(age)} (the limit is ${formatAge(KUBELET_HEARTBEAT_TIMEOUT_MS)}).`,
  };
}

/** Statuses in which Talos is installed on the box's disk (deployment-service bootMediaRotation.ts). */
const INSTALLED_STATUSES: ReadonlySet<string> = new Set([
  'bootstrapping', 'installing_prereqs', 'joining', 'ready', 'cordoned', 'upgrading', 'error',
]);

/**
 * Whether the box reconnects to Jeket with its own key rather than its site's
 * USB credential (JSS#890) — whether "Replace USB image" leaves it alone.
 * Never `fail` or `warn`: a box not secured yet is healthy, and Jeket secures
 * it automatically. `ok` only once it has actually reconnected with its key
 * (`verifiedAt`), the same test the replace preview uses.
 */
export function reconnectIndicator(m: Machine | null): Indicator {
  const base = { key: 'reconnect' as const, name: 'Secured' };
  if (!m) return { ...base, state: 'na', short: 'not tracked', detail: 'This node has no Jeket machine record.' };
  if (m.reconnect === undefined) {
    return { ...base, state: 'na', short: '—', detail: 'The Jeket cloud did not report whether this box has its own reconnect key.' };
  }
  if (m.reconnect?.verifiedAt) {
    return {
      ...base,
      state: 'ok',
      short: 'own key',
      detail: "The box reconnects to Jeket with its own key, not the site's USB image. Replacing the USB image does not affect it.",
    };
  }
  if (!INSTALLED_STATUSES.has(m.status)) {
    return { ...base, state: 'na', short: 'not installed', detail: 'The box is secured with its own key once it has installed.' };
  }
  return {
    ...base,
    state: 'unknown',
    short: 'not yet',
    detail:
      "The box still reconnects with the site's USB credential. Jeket secures it automatically, and Replace USB image " +
      'secures it first.',
  };
}

export function boxIndicators(box: Box, nodesState: NodesState, now: number): Indicator[] {
  return [
    siderolinkIndicator(box.machine, now),
    talosApiIndicator(box.machine, now),
    kubeletIndicator(box, nodesState, now),
    reconnectIndicator(box.machine),
  ];
}

/**
 * The one line under the status: the recorded error when there is one (it
 * names the cause), else the first failing indicator, else — while the box is
 * still being set up or stopped — what its status means. Boxes never wait on
 * the license (it is checked when JBS is installed, JSS#864), so there is no
 * license line.
 */
export function boxStatusReason(box: Box, indicators: Indicator[]): string | null {
  if (box.machine?.errorMessage) return box.machine.errorMessage;
  const failing = indicators.find((i) => i.state === 'fail');
  if (failing) return `${failing.name}: ${failing.detail}`;
  if (!box.machine) {
    return 'Kubernetes node with no Jeket machine record. It was not set up through this site, or its record was removed.';
  }
  const d = describeMachineStatus(box.machine.status);
  return d.tone === 'healthy' ? null : d.description;
}

// ---- Identity and versions ----

export interface HardwareIdentity {
  /** SMBIOS product, e.g. "NVIDIA DGX Spark"; null when not captured (#830). */
  product: string | null;
  cpu: string | null;
  gpus: string[];
  memory: string | null;
  arch: string | null;
}

function gib(bytes: number): string {
  return `${Math.round(bytes / (1024 ** 3))} GiB`;
}

export function hardwareIdentity(box: Pick<Box, 'machine' | 'node'>): HardwareIdentity {
  // `hardware` is `{}` between a reinstall and rediscovery; read defensively.
  const hw = (box.machine?.hardware ?? {}) as Partial<Machine['hardware']>;
  const nodeHw = box.node?.hardware;

  let product: string | null = null;
  const productName = hw.system?.productName?.trim();
  if (productName) {
    product = productName.replace(/_/g, ' ');
    const maker = hw.system?.manufacturer?.trim();
    if (maker && !product.toLowerCase().startsWith(maker.toLowerCase())) product = `${maker} ${product}`;
  }

  const cpuModel = hw.cpu || nodeHw?.cpu || '';
  const cores = hw.cpuCores || nodeHw?.cpuCores || 0;
  const cpu = cpuModel ? `${cpuModel}${cores ? ` (${cores} cores)` : ''}` : cores ? `${cores} cores` : null;

  const gpuList = hw.gpus?.length ? hw.gpus : nodeHw?.gpus ?? [];
  const gpus = gpuList.map((g) => g.name).filter(Boolean);

  const memBytes = hw.memoryBytes || nodeHw?.memoryBytes || 0;
  return {
    product,
    cpu,
    gpus,
    memory: memBytes > 0 ? gib(memBytes) : null,
    arch: hw.arch || box.node?.architecture || null,
  };
}

function bareVersion(v: string | null | undefined): string | null {
  const t = v?.trim().replace(/^v/i, '');
  return t ? t : null;
}

export interface BoxVersions {
  /** Bare, e.g. "1.13.10". */
  talos: string | null;
  kubernetes: string | null;
}

/**
 * Versions the box actually runs. Talos from the live node (osImage), else
 * the machine row once apid has answered (cluster-monitor reconciles it from
 * the node's own version(), #792) — before that the row holds the site's
 * version at provision time, which says nothing about this box. Kubernetes
 * only from the live node.
 */
export function boxVersions(box: Pick<Box, 'machine' | 'node'>): BoxVersions {
  const observedTalos = box.machine?.apidLastOkAt ? box.machine.talosVersion : null;
  return {
    talos: bareVersion(box.node?.talosVersion) ?? bareVersion(observedTalos),
    kubernetes: bareVersion(box.node?.kubeletVersion),
  };
}
